/**
 * Landing Page Content Variants
 *
 * Story 6.6: A/B Variant Refinement & Admin Analytics
 *
 * Value proposition content variants for A/B testing.
 * Content sourced from 1-2-value-proposition.md marketing brief.
 *
 * Each variant type (headline, CTA, subhead) can be independently tested
 * via PostHog feature flags without changing the page layout variant.
 */

import type {
  HeadlineVariant,
  CTAVariant,
  SubheadVariant,
} from '@/lib/posthogABTesting';

// ============================================================================
// Headline Variants
// ============================================================================

export interface HeadlineContent {
  text: string;
  angle: string;
  i18nKey: string;
}

/**
 * Headline content variants for A/B testing
 *
 * | Variant | Angle | Text |
 * |---------|-------|------|
 * | emotional | Conversational | "Tell me about your thing and I'll make everyone know" |
 * | speed | Speed-focused | "Create a free chatbot in 2 minutes" |
 * | pain | Pain relief | "Stop answering the same questions over and over" |
 * | aspirational | Aspirational | "Your event deserves a bot" |
 * | social_proof | Social proof | "Trusted by 1,000+ organizers" |
 */
export const HEADLINE_CONTENT: Record<HeadlineVariant, HeadlineContent> = {
  emotional: {
    text: "Tell me about your thing and I'll make everyone know",
    angle: 'Conversational',
    i18nKey: 'landing.headline.emotional',
  },
  speed: {
    text: 'Create a free chatbot in 2 minutes',
    angle: 'Speed-focused',
    i18nKey: 'landing.headline.speed',
  },
  pain: {
    text: 'Stop answering the same questions over and over',
    angle: 'Pain relief',
    i18nKey: 'landing.headline.pain',
  },
  aspirational: {
    text: 'Your event deserves a bot',
    angle: 'Aspirational',
    i18nKey: 'landing.headline.aspirational',
  },
  social_proof: {
    text: 'Trusted by 1,000+ organizers',
    angle: 'Social proof',
    i18nKey: 'landing.headline.socialProof',
  },
};

// ============================================================================
// CTA Button Variants
// ============================================================================

export interface CTAContent {
  text: string;
  angle: string;
  i18nKey: string;
}

/**
 * CTA button content variants for A/B testing
 *
 * | Variant | Angle | Text |
 * |---------|-------|------|
 * | action | Action-focused | "Create Your Free Bot" |
 * | low_commitment | Low barrier | "Try It Free" |
 * | speed_focused | Speed-focused | "Start in 2 Minutes" |
 * | curiosity | Curiosity-driven | "See How It Works" |
 */
export const CTA_CONTENT: Record<CTAVariant, CTAContent> = {
  action: {
    text: 'Create Your Free Bot',
    angle: 'Action-focused',
    i18nKey: 'landing.cta.action',
  },
  low_commitment: {
    text: 'Try It Free',
    angle: 'Low barrier',
    i18nKey: 'landing.cta.lowCommitment',
  },
  speed_focused: {
    text: 'Start in 2 Minutes',
    angle: 'Speed-focused',
    i18nKey: 'landing.cta.speedFocused',
  },
  curiosity: {
    text: 'See How It Works',
    angle: 'Curiosity-driven',
    i18nKey: 'landing.cta.curiosity',
  },
};

// ============================================================================
// Subhead Variants
// ============================================================================

export interface SubheadContent {
  text: string;
  angle: string;
  i18nKey: string;
}

/**
 * Subhead content variants for A/B testing
 *
 * | Variant | Angle | Text |
 * |---------|-------|------|
 * | no_signup | Low barrier | "100% free. No signup. No credit card. No catch." |
 * | speed | Speed | "From idea to shareable link in under 2 minutes." |
 * | use_cases | Use cases | "Perfect for events, services, or personal brands." |
 * | value | Value prop | "Your attendees get answers 24/7. You get your time back." |
 */
export const SUBHEAD_CONTENT: Record<SubheadVariant, SubheadContent> = {
  no_signup: {
    text: '100% free. No signup. No credit card. No catch.',
    angle: 'Low barrier',
    i18nKey: 'landing.subhead.noSignup',
  },
  speed: {
    text: 'From idea to shareable link in under 2 minutes.',
    angle: 'Speed',
    i18nKey: 'landing.subhead.speed',
  },
  use_cases: {
    text: 'Perfect for events, services, or personal brands.',
    angle: 'Use cases',
    i18nKey: 'landing.subhead.useCases',
  },
  value: {
    text: 'Your attendees get answers 24/7. You get your time back.',
    angle: 'Value prop',
    i18nKey: 'landing.subhead.value',
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get headline text for a variant
 */
export function getHeadlineText(variant: HeadlineVariant): string {
  return HEADLINE_CONTENT[variant].text;
}

/**
 * Get CTA text for a variant
 */
export function getCTAText(variant: CTAVariant): string {
  return CTA_CONTENT[variant].text;
}

/**
 * Get subhead text for a variant
 */
export function getSubheadText(variant: SubheadVariant): string {
  return SUBHEAD_CONTENT[variant].text;
}

/**
 * Get i18n key for headline variant
 */
export function getHeadlineI18nKey(variant: HeadlineVariant): string {
  return HEADLINE_CONTENT[variant].i18nKey;
}

/**
 * Get i18n key for CTA variant
 */
export function getCTAI18nKey(variant: CTAVariant): string {
  return CTA_CONTENT[variant].i18nKey;
}

/**
 * Get i18n key for subhead variant
 */
export function getSubheadI18nKey(variant: SubheadVariant): string {
  return SUBHEAD_CONTENT[variant].i18nKey;
}
